import React, { useState } from "react";
import { EMAILS, MAILBOX_KPIS, TRIAGE_SUMMARY } from "./data";
import { ClassBadge, Eyebrow, GradButton, Ic, KpiRow, usePrefersReducedMotion } from "./shared";

interface GroupMailboxViewProps {
  triaged: boolean;
  onTriage: (triaged: boolean) => void;
  onOpenCase: (id: string) => void;
  closedIds: string[];
}

export function GroupMailboxView({ triaged, onTriage, onOpenCase, closedIds }: GroupMailboxViewProps) {
  const [triaging, setTriaging] = useState(false);
  const reduceMotion = usePrefersReducedMotion();

  const runTriage = () => {
    if (triaged || reduceMotion) {
      onTriage(true);
      return;
    }
    setTriaging(true);
    window.setTimeout(() => {
      setTriaging(false);
      onTriage(true);
    }, 1100);
  };

  return (
    <div className="gmv-root">
      <KpiRow items={MAILBOX_KPIS} />
      <div className="gmv-card">
        <div className="gmv-card-head">
          <div className="gmv-head-left">
            <Eyebrow>Shared Mailbox – AP.PAYMENTS@COMPANY.COM</Eyebrow>
            <p className="gmv-head-copy">Polled every 2 minutes. The classifier reads subject and body, extracts PO / invoice identifiers and decides whether the query belongs to the agent.</p>
          </div>
          <div className="gmv-head-action">
            <GradButton onClick={runTriage} disabled={triaging}>
              <Ic.sparkles width={14} height={14} className={triaging ? "np-spin" : ""} />
              {triaging ? "Classifying…" : triaged ? "Re-run AI triage" : "Run AI triage"}
            </GradButton>
          </div>
        </div>
        <div className="gmv-table-wrap">
          <div className="gmv-table-head">
            <span className="gmv-head-col gmv-col-mail">Mail</span>
            <span className="gmv-head-col gmv-col-sender">Sender</span>
            <span className="gmv-head-col gmv-col-subject">Subject &amp; Extracted Context</span>
            <span className="gmv-head-col gmv-col-received">Received</span>
            <span className={triaged ? "gmv-head-col gmv-col-classification is-triaged" : "gmv-head-col gmv-col-classification"}>AI Classification</span>
          </div>
          {EMAILS.map((email) => {
            const closed = closedIds.includes(email.id);
            const canOpen = !!email.openable && !closed;
            const open = () => onOpenCase(email.id);
            return (
              <div key={email.id} role={canOpen ? "button" : undefined} tabIndex={canOpen ? 0 : undefined}
                onClick={canOpen ? open : undefined}
                onKeyDown={canOpen ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); } } : undefined}
                className={`${canOpen ? "np-row " : ""}gmv-row${canOpen ? " is-openable" : ""}${closed ? " is-closed" : ""}`}>
                <span className="gmv-cell-mail">{email.id}</span>
                <div className="gmv-cell-sender">
                  <span className="gmv-primary-text gmv-truncate">{email.sender}</span>
                  <span className="gmv-secondary-text">{email.email}</span>
                </div>
                <div className="gmv-cell-subject">
                  <span className="gmv-primary-text gmv-truncate">{email.subject}</span>
                  <span className="gmv-secondary-text gmv-truncate">{triaged ? email.keyIds : email.preview}</span>
                </div>
                <span className="gmv-cell-received">{email.received}</span>
                <div className={triaged ? "gmv-cell-classification is-triaged" : "gmv-cell-classification"}>
                  {closed ? (
                    <span className="gmv-pill gmv-pill--closed">Closed</span>
                  ) : triaged ? (
                    <ClassBadge kind={email.kind} text={email.cls} />
                  ) : (
                    <span className="gmv-pill gmv-pill--unclassified">Unclassified</span>
                  )}
                  {canOpen && <Ic.chevronRight width={14} height={14} className="gmv-chevron" />}
                </div>
              </div>
            );
          })}
        </div>
        {triaged && (
          <div className="np-fade gmv-summary">
            <div className="gmv-summary-head">
              <Ic.sparkles width={14} height={14} className="gmv-summary-icon" />
              <span className="gmv-summary-title">AI Triage Summary</span>
            </div>
            <p className="gmv-summary-body">{TRIAGE_SUMMARY}</p>
          </div>
        )}
      </div>
    </div>
  );
}
